import { Component } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * App-level error boundary.
 *
 * Catches render-time errors in any descendant component so a single page
 * crash never takes down the whole app (e.g. an issue on the Admin Users
 * page should NEVER block the public Ramp Calculator from loading).
 *
 * Falls back to a clean recovery UI with a "Reload" action. In dev builds
 * the underlying error message is shown to help debugging; in prod builds
 * we keep it short for the customer-facing public route.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Keep dev-only logging; never leak in production builds.
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.error("[ErrorBoundary] Caught render error:", error, info);
    }
  }

  handleReload = () => {
    // Hard reload to recover state cleanly.
    if (typeof window !== "undefined") window.location.reload();
  };

  handleHome = () => {
    if (typeof window !== "undefined") window.location.href = "/ramp-calculator";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isDev = process.env.NODE_ENV !== "production";
    const msg = this.state.error?.message || "Something went wrong.";

    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4" data-testid="app-error-boundary">
        <div className="max-w-md w-full bg-white border border-orange-200 rounded-xl shadow-md p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-orange-100 text-orange-700 flex items-center justify-center mb-4">
            <AlertTriangle size={26} />
          </div>
          <h1 className="text-xl font-bold text-[#0A1128] mb-2">Something went wrong</h1>
          <p className="text-sm text-slate-600 leading-relaxed">
            We hit an unexpected error rendering this page. The calculator engine and your data are safe.
            Reload to try again, or head back to the calculator.
          </p>
          {isDev && (
            <pre className="mt-4 text-[11px] text-left text-red-700 bg-red-50 border border-red-200 rounded p-3 overflow-auto max-h-32 font-mono">
              {msg}
            </pre>
          )}
          <div className="flex gap-2 mt-6 justify-center">
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#254391] hover:bg-[#1E3675] text-white font-semibold text-sm rounded-md transition-colors"
            >
              <RefreshCw size={14} /> Reload
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              data-testid="error-boundary-home"
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 hover:border-[#254391] hover:text-[#254391] text-gray-700 font-semibold text-sm rounded-md transition-colors"
            >
              Back to Calculator
            </button>
          </div>
        </div>
      </div>
    );
  }
}
